import { useState, useEffect } from "react";
import axios from "axios";
import { FileArrowDown, Receipt, ShoppingCart, Wallet, Clock, ChartLineUp, BookOpen } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { ExportButtons } from "@/components/reports/ExportButtons";

const API = `${window.location.origin}/api`;
const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const PRESETS = [
  { key: "month", label: "This Month", range: (n) => [fmt(new Date(n.getFullYear(), n.getMonth(), 1)), fmt(n)] },
  { key: "last", label: "Last Month", range: (n) => [fmt(new Date(n.getFullYear(), n.getMonth() - 1, 1)), fmt(new Date(n.getFullYear(), n.getMonth(), 0))] },
  { key: "fy", label: "This FY", range: (n) => { const y = n.getMonth() >= 3 ? n.getFullYear() : n.getFullYear() - 1; return [fmt(new Date(y, 3, 1)), fmt(n)]; } },
];

const REPORT_LIST = [
  { type: "sales", label: "Sales Report", desc: "Every bill with GST, received & balance", icon: Receipt, tone: "text-green-700 bg-green-50" },
  { type: "purchase", label: "Purchase Report", desc: "Supplier invoices, batches, KG & GST", icon: ShoppingCart, tone: "text-amber-700 bg-amber-50" },
  { type: "expense", label: "Expense Report", desc: "All expenses with category totals", icon: Wallet, tone: "text-red-700 bg-red-50" },
  { type: "pending", label: "Pending Payments", desc: "Outstanding customer dues with aging", icon: Clock, tone: "text-orange-700 bg-orange-50" },
  { type: "profit-loss", label: "Profit & Loss", desc: "Sales - Purchases - Expenses, plus GST", icon: ChartLineUp, tone: "text-indigo-700 bg-indigo-50" },
  { type: "customer-ledger", label: "Customer Ledger", desc: "Bills, payments & running balance", icon: BookOpen, tone: "text-blue-700 bg-blue-50", needsCustomer: true },
];

export function ReportsExportsPanel({ defaultFrom, defaultTo }) {
  const [range, setRange] = useState({ date_from: defaultFrom, date_to: defaultTo });
  const [customer, setCustomer] = useState("");
  const [customers, setCustomers] = useState([]);

  useEffect(() => { axios.get(`${API}/reports/customers/list`).then((r) => setCustomers(r.data)).catch(() => {}); }, []);

  const applyPreset = (p) => { const [date_from, date_to] = p.range(new Date()); setRange({ date_from, date_to }); };

  return (
    <Card className="p-4 sm:p-5 border-2 border-indigo-100 bg-white" data-testid="reports-exports-panel">
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base sm:text-lg font-black text-gray-900 flex items-center gap-2" style={{ fontFamily: "'Gupter', serif" }}>
            <FileArrowDown size={20} className="text-indigo-600" weight="bold" /> Reports & Exports
          </h2>
          <p className="text-xs text-gray-500">Download any report as PDF or Excel for the selected period</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input type="date" value={range.date_from} onChange={(e) => setRange((r) => ({ ...r, date_from: e.target.value }))} className="h-8 w-[140px] text-xs" data-testid="report-date-from" />
          <span className="text-xs text-gray-400">to</span>
          <Input type="date" value={range.date_to} onChange={(e) => setRange((r) => ({ ...r, date_to: e.target.value }))} className="h-8 w-[140px] text-xs" data-testid="report-date-to" />
          {PRESETS.map((p) => (
            <button key={p.key} onClick={() => applyPreset(p)} className="h-8 px-2.5 rounded-full border border-indigo-200 text-[11px] font-bold text-indigo-700 hover:bg-indigo-50 transition-colors" data-testid={`report-preset-${p.key}`}>{p.label}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {REPORT_LIST.map((r) => (
          <div key={r.type} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-indigo-300 transition-colors" data-testid={`report-row-${r.type}`}>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${r.tone}`}><r.icon size={18} weight="bold" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-gray-900">{r.label}</p>
              {r.needsCustomer ? (
                <>
                  <Input list="ledger-customers" value={customer} onChange={(e) => setCustomer(e.target.value)} placeholder="Type or pick customer..." className="h-7 mt-1 text-xs" data-testid="ledger-customer-input" />
                  <datalist id="ledger-customers">{customers.map((c) => <option key={c} value={c} />)}</datalist>
                </>
              ) : <p className="text-[11px] text-gray-500 truncate">{r.desc}</p>}
            </div>
            <ExportButtons
              type={r.type}
              params={r.needsCustomer ? { ...range, customer_name: customer } : range}
              testPrefix={`export-${r.type}`}
              disabledReason={r.needsCustomer && !customer.trim() ? "Please select a customer for the ledger" : ""}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
